import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart3, Phone, PhoneCall, PhoneOff, Voicemail, Calendar, Clock,
  TrendingUp, Target, Users, CheckCircle2, ArrowUp, ArrowDown
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';

const API = process.env.REACT_APP_BACKEND_URL;

// Status colors for call outcomes
const statusColors = {
  answered: 'bg-green-100 text-green-700',
  meeting_booked: 'bg-blue-100 text-blue-700',
  demo_scheduled: 'bg-blue-100 text-blue-700',
  interested: 'bg-emerald-100 text-emerald-700',
  callback_requested: 'bg-yellow-100 text-yellow-700',
  not_interested: 'bg-orange-100 text-orange-700',
  voicemail_dropped: 'bg-purple-100 text-purple-700',
  no_answer: 'bg-gray-100 text-gray-700',
  busy: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
};

export default function AICallAnalytics() {
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchOverview();
    fetchCampaigns();
  }, []);
  
  useEffect(() => {
    if (selectedCampaign) {
      fetchCampaignAnalytics(selectedCampaign);
    } else {
      setCampaignAnalytics(null);
    }
  }, [selectedCampaign]);
  
  const fetchOverview = async () => {
    try {
      const response = await axios.get(`${API}/api/ai-calls/analytics/overview`);
      setOverview(response.data);
    } catch (error) {
      console.error('Error fetching overview:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/api/ai-calls/analytics/campaigns`);
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };
  
  const fetchCampaignAnalytics = async (campaignId) => {
    try {
      const response = await axios.get(`${API}/api/ai-calls/analytics/campaigns/${campaignId}`);
      setCampaignAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching campaign analytics:', error);
    }
  };
  
  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return '12 PM';
    return `${hour - 12} PM`;
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="ai-call-analytics">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Calls</p>
                <p className="text-3xl font-bold">{overview?.summary?.total_calls || 0}</p>
              </div>
              <Phone className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Answer Rate</p>
                <p className="text-3xl font-bold text-green-600">{overview?.summary?.answer_rate || 0}%</p>
              </div>
              <PhoneCall className="h-8 w-8 text-green-500 opacity-50" />
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {overview?.summary?.calls_answered || 0} answered
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Conversion Rate</p>
                <p className="text-3xl font-bold text-purple-600">{overview?.summary?.conversion_rate || 0}%</p>
              </div>
              <Target className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {overview?.summary?.meetings_booked || 0} meetings booked
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Avg Duration</p>
                <p className="text-3xl font-bold">{formatDuration(overview?.summary?.avg_duration_seconds)}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500 opacity-50" />
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {overview?.summary?.active_campaigns || 0} active campaigns
            </p>
          </CardContent>
        </Card>
      </div>
      
      {/* Status Breakdown */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              Call Outcomes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(overview?.status_breakdown || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <div className="flex items-center gap-2">
                    <Badge className={statusColors[status] || 'bg-gray-100 text-gray-700'}>
                      {status.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{count}</span>
                    <span className="text-xs text-slate-400">
                      ({overview?.summary?.total_calls > 0 ? Math.round(count / overview.summary.total_calls * 100) : 0}%)
                    </span>
                  </div>
                </div>
              ))}
              {Object.keys(overview?.status_breakdown || {}).length === 0 && (
                <p className="text-slate-400 text-center py-4">No call data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              Calls Over Time (30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(overview?.calls_timeline || []).length > 0 ? (
              <div className="h-48 flex items-end gap-1">
                {overview.calls_timeline.slice(-14).map((day, idx) => {
                  const maxCalls = Math.max(...overview.calls_timeline.map(d => d.calls));
                  const height = maxCalls > 0 ? (day.calls / maxCalls * 100) : 0;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                        style={{ height: `${Math.max(height, 2)}%` }}
                        title={`${day.date}: ${day.calls} calls`}
                      ></div>
                      <span className="text-[8px] text-slate-400 mt-1 rotate-45">
                        {day.date.slice(5)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400">
                No call history yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Campaign Comparison */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-500" />
              Campaign Performance
            </CardTitle>
            <Select value={selectedCampaign || 'all'} onValueChange={(v) => setSelectedCampaign(v === 'all' ? null : v)}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Select campaign for details" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Campaigns (Overview)</SelectItem>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {!selectedCampaign ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Leads</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Answered</TableHead>
                  <TableHead className="text-right">Meetings</TableHead>
                  <TableHead className="text-right">Answer Rate</TableHead>
                  <TableHead className="text-right">Conversion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedCampaign(campaign.id)}>
                    <TableCell className="font-medium">{campaign.name}</TableCell>
                    <TableCell>
                      <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'}>
                        {campaign.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{campaign.total_leads}</TableCell>
                    <TableCell className="text-right">{campaign.total_calls}</TableCell>
                    <TableCell className="text-right">{campaign.calls_answered}</TableCell>
                    <TableCell className="text-right text-green-600 font-medium">{campaign.meetings_booked}</TableCell>
                    <TableCell className="text-right">
                      <span className={campaign.answer_rate >= 30 ? 'text-green-600' : campaign.answer_rate >= 15 ? 'text-yellow-600' : 'text-red-600'}>
                        {campaign.answer_rate}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={campaign.conversion_rate >= 20 ? 'text-green-600' : campaign.conversion_rate >= 10 ? 'text-yellow-600' : 'text-orange-600'}>
                        {campaign.conversion_rate}%
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
                {campaigns.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                      No campaigns yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          ) : campaignAnalytics ? (
            <CampaignDetails analytics={campaignAnalytics} onBack={() => setSelectedCampaign(null)} />
          ) : (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Campaign Details Component
function CampaignDetails({ analytics, onBack }) {
  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return '12 PM';
    return `${hour - 12} PM`;
  };
  
  return (
    <div className="space-y-6">
      {/* Campaign Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{analytics.campaign.name}</h3>
          <p className="text-sm text-slate-500">Campaign Analytics</p>
        </div>
        <button 
          onClick={onBack}
          className="text-sm text-blue-600 hover:underline"
        >
          ← Back to all campaigns
        </button>
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="text-center p-3 bg-blue-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{analytics.summary.leads_called}</div>
          <div className="text-xs text-slate-500">Leads Called</div>
          <div className="text-xs text-slate-400">of {analytics.summary.total_leads}</div>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg">
          <div className="text-2xl font-bold text-green-600">{analytics.summary.calls_answered}</div>
          <div className="text-xs text-slate-500">Answered</div>
        </div>
        <div className="text-center p-3 bg-purple-50 rounded-lg">
          <div className="text-2xl font-bold text-purple-600">{analytics.summary.meetings_booked}</div>
          <div className="text-xs text-slate-500">Meetings Booked</div>
        </div>
        <div className="text-center p-3 bg-orange-50 rounded-lg">
          <div className="text-2xl font-bold text-orange-600">{formatDuration(analytics.summary.avg_duration_seconds)}</div>
          <div className="text-xs text-slate-500">Avg Duration</div>
        </div>
      </div>
      
      {/* Best Time to Call */}
      {analytics.best_hours_to_call && analytics.best_hours_to_call.length > 0 && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Best Times to Call
          </h4>
          <div className="flex gap-2 flex-wrap">
            {analytics.best_hours_to_call.map((item, idx) => (
              <Badge key={idx} className="bg-green-100 text-green-700">
                {formatHour(item.hour)} ({item.rate}% answer rate)
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      {/* Outcome Breakdown */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium mb-2">Status Breakdown</h4>
          <div className="space-y-1">
            {Object.entries(analytics.status_breakdown || {}).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between text-sm">
                <span className="capitalize">{status.replace(/_/g, ' ')}</span>
                <span className="font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-medium mb-2">Outcome Breakdown</h4>
          <div className="space-y-1">
            {Object.entries(analytics.outcome_breakdown || {}).map(([outcome, count]) => (
              <div key={outcome} className="flex items-center justify-between text-sm">
                <span className="capitalize">{outcome.replace(/_/g, ' ')}</span>
                <span className="font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Recent Calls */}
      <div>
        <h4 className="font-medium mb-2">Recent Calls</h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Lead</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Follow-up</TableHead>
              <TableHead>Time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {analytics.recent_calls.map((call) => (
              <TableRow key={call.id}>
                <TableCell className="font-medium">{call.lead_name}</TableCell>
                <TableCell className="text-slate-500">{call.company}</TableCell>
                <TableCell>
                  <Badge className={statusColors[call.status] || 'bg-gray-100'}>
                    {call.status?.replace(/_/g, ' ')}
                  </Badge>
                </TableCell>
                <TableCell>{formatDuration(call.duration_seconds)}</TableCell>
                <TableCell>
                  {call.follow_up_email_sent ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <span className="text-slate-300">-</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-slate-500">
                  {call.created_at ? new Date(call.created_at).toLocaleString() : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
